import { app, ENV_LOCAL } from '../../../config'
import { getAuthenticatedUserToken } from '../../auth'
import { cleanTitle, queryStringFromObj } from '../helpers/Strings'

// Remble
const REMBLE_API_URL = `${app.api.url}/v${app.api.version}`

// WordPress
const WP_REST_BASE_URL = `${app.wp_api.url}/wp-json` // plain url: ${app.api.url}?rest_route=
const WP_API_URL = `${WP_REST_BASE_URL}/wp/v${app.wp_api.wp_version}`
const WP_API_PUBLIC_URL = `/wp/v${app.wp_api.wp_version}`
const REMBLE_WP_API_URL = `${WP_REST_BASE_URL}/remble/v${app.wp_api.remble_version}`
const LMS_API_URL = `${WP_REST_BASE_URL}/ldlms/v${app.wp_api.lms_version}`
const LMS_REST_BASE_QUERY_CHAR = '?' // If using pretty links, use '?' otherwise, use '&'

// LMS course object types
const TYPE_COURSE = 'course'
const TYPE_LESSON = 'lesson'
const TYPE_TOPIC = 'topic'
const TYPE_QUIZ = 'quiz'
const NO_LIMIT = 200 // LearnDash/WP does not let you request more than 200 rows at a time (depending on the endpoint overrides)

// Practices API practice keys
// NOTE: These key values MUST match the key values defined for each practice in `config.js`
const KEY_PRACTICE_AFFIRMATIONS = 'affirmation'
const KEY_PRACTICE_BREATHWORK = 'breathing'
const KEY_PRACTICE_COPING_SKILLS = 'coping'
const KEY_PRACTICE_DAILY_REMBLE = 'daily-remble'
const KEY_PRACTICE_JOURNALING = 'journaling'
const KEY_PRACTICE_MEDITATIONS = 'meditation'
const KEY_PRACTICE_RELATIONSHIPQA = 'question'
const KEY_PRACTICE_COMPLIMENTS = 'compliments'
const KEY_PRACTICE_DATE_IDEAS = 'date_ideas'
const KEY_PRACTICE_JOURNEYS = 'journey'
const KEY_PRACTICE_SESSIONS = 'session'

// WordPress API practice keys
const KEY_PRACTICE_AFFIRMATIONS_WP = 'affirmation_activity'
const KEY_PRACTICE_BREATHWORK_WP = 'breathing_activity'
const KEY_PRACTICE_COPING_SKILLS_WP = 'coping_activity'
const KEY_PRACTICE_JOURNALING_WP = 'journaling_activity'
const KEY_PRACTICE_MEDITATIONS_WP = 'meditation_activity'
const KEY_PRACTICE_RELATIONSHIPQA_WP = 'question_activity'
const KEY_PRACTICE_COMPLIMENTS_WP = 'compliments'
const KEY_PRACTICE_DATE_IDEAS_WP = 'date_ideas'
const KEY_PRACTICE_DAILY_REMBLE_WP = 'daily_remble'
const KEY_ARTICLES_WP = 'post'

const KEY_PREFERENCE_AFFIRMATIONS = 'affirmations'
const KEY_PREFERENCE_BREATHWORK = 'breathwork'
const KEY_PREFERENCE_COPING_SKILLS = 'coping-skills'
const KEY_PREFERENCE_JOURNALING = 'journaling'
const KEY_PREFERENCE_MEDITATIONS = 'meditations'
const KEY_PREFERENCE_RELATIONSHIPQA = 'relationship-questions'

const KEY_PRACTICES_PREFERENCES_FIELD = 'practices-preferences'

// Exception Strings
const ERR_CODE_JWT_OBSOLETE = 'jwt_auth_obsolete_token'
const ERR_CODE_JWT_INVALID = 'jwt_auth_invalid_token'

const ERR_USERNAME_ALREADY_REGISTERED = 'Sorry, that username already exists!'
const ERR_USER_NOT_CONFIRMED = 'User is not confirmed.'
const ERR_USER_ALREADY_CONFIRMED =
  'User cannot be confirmed. Current status is CONFIRMED'
const ERR_JWT_OBSOLETE =
  'Your authorization token has expired. Please logout and login again.'
const ERR_COULD_NOT_CONNECT_TO_SERVER =
  'Could not connect to remote server. Contact Remble support and try again later.'

// Server Sent Events
const SSE_EVENT_TYPE_MESSAGE = 'message'
const SSE_EVENT_TYPE_ERROR = 'error'
const STREAM_COMPLETE = '[DONE]'

let _sse_token = null

/* =============================================
 * Public API helper methods
 * ============================================= */

const getChatToken = async () => {
  if (!_sse_token) {
    const url = `${REMBLE_API_URL}/chat/token`
    const result = await fetch(url, getRESTParams()).then(res => res.json())

    // Cache the token
    _sse_token = result.token
  }

  return _sse_token
}

/**
 * Builds a request URL by converting the passed queryObj to key=value pairs
 * and appending it to the passed in url string
 * @param {String} url The base url to append the query string to
 * @param {Object} queryObj The object to convert to key=value pairs
 */
const buildRequestURL = (url, queryObj) => {
  // Return early if we passed an empty queryObj
  if (!queryObj) return url

  // Make a copy
  const props = { ...queryObj }

  // Drop empty values and urlencode URI characters
  Object.keys(props).forEach(key => {
    // Allow "0" zero values, but drop empty strings
    if (props[key] !== 0 && !props[key]) delete props[key]
    else
      props[key] = ('' + props[key])
        .replace('?', '%3F')
        .replace('&', '%26')
        .replace('#', '%23')
  })

  return url + queryStringFromObj(props, LMS_REST_BASE_QUERY_CHAR)
}

/**
 * API helper to get and merge common GET REST params.
 * @param {Object} params Parameter object to merge with and override common params.
 * @returns A JSON object containing the desired configuration params
 */
const getRESTParams = (params = {}, headers = {}) => {
  const token = getAuthenticatedUserToken()

  // See https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
  // Default options are marked with *
  return {
    method: 'GET', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: app.environment === ENV_LOCAL ? 'reload' : 'default', // *default, no-cache, reload, force-cache, only-if-cached
    headers: {
      Origin: app.url,
      Authorization: `Bearer ${token}`,
      ...headers
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'origin', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    ...params // overrides, if applicable
  }
}

/**
 * Simple debounce helper function
 * See https://www.freecodecamp.org/news/javascript-debounce-example/
 * @param {Function} func The function to debounce
 * @param {Number} timeout The debounce timeout/threshold
 */
const debounce = async (func, timeout = 300) => {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => func.apply(this, args), timeout)
  }
}

/**
 * API helper to get and merge common POST REST params.
 * @param {Object} params Parameter object to merge with and override common params.
 */
const postRESTParams = (params = {}, headers = {}) =>
  getRESTParams(
    {
      method: 'POST',
      cache: 'no-cache',
      ...params
    },
    headers
  )

/**
 * API helper to get and merge common PATCH REST params.
 * @param {Object} params Parameter object to merge with and override common params.
 * @param {Object} headers The headers to merge with and override common headers.
 */
const patchRESTParams = (params = {}, headers = {}) =>
  getRESTParams(
    {
      method: 'PATCH',
      cache: 'no-cache',
      ...params
    },
    headers
  )

/**
 * API helper to get and merge common DELETE REST params.
 * @param {Object} params Parameter object to merge with and override common params.
 * @param {Object} headers The headers to merge with and override common headers.
 */
const deleteRESTParams = (params = {}, headers = {}) =>
  getRESTParams(
    {
      method: 'DELETE',
      cache: 'no-cache',
      ...params
    },
    headers
  )

/**
 * Common LearnDash LMS request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} queryObj Optional arguments to pass as query string args for filtering request responses
 * @param {Object} paramObj Parameter object to merge with common REST params, if necessary.
 * See getRESTParams method for more info.
 */
const learnDashGET = async (url, queryObj, paramObj) => {
  // Prevent cache on local
  if (app.environment === ENV_LOCAL) {
    queryObj = { ...queryObj, no_cache: true }
    console.log(
      'NOTE: ENV_LOCAL detected. `no_cache` override enabled in `learnDashGET`.'
    )
  }

  const requestUrl = buildRequestURL(url, queryObj)
  const params = getRESTParams(paramObj)
  const result = await fetch(requestUrl, params)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  // JWT expired
  if (result?.code === ERR_CODE_JWT_OBSOLETE) throw new Error(ERR_JWT_OBSOLETE)

  return result
}

/**
 * Common Practices request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} queryObj Optional arguments to pass as query string args for filtering request responses
 * @param {Object} paramObj Parameter object to merge with common REST params, if necessary.
 * See getRESTParams method for more info.
 */
const practicesGET = async (url, queryObj, paramObj) => {
  const requestUrl = buildRequestURL(url, queryObj)
  const params = getRESTParams(paramObj)
  const result = await fetch(requestUrl, params)
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
    })

  // JWT expired
  if (result?.code === ERR_CODE_JWT_OBSOLETE) throw new Error(ERR_JWT_OBSOLETE)

  // JWT invalid
  if (result?.code === ERR_CODE_JWT_INVALID)
    throw new Error(ERR_CODE_JWT_INVALID)

  return result
}

/**
 * Practices post function.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const practicesPOST = async (url, params) =>
  practicesGET(
    url,
    {},
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/**
 * Practices patch function.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const practicesPATCH = async (url, params) =>
  practicesGET(
    url,
    {},
    patchRESTParams(params, { 'Content-Type': 'application/json' })
  )

/**
 * Practices delete function.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const practicesDELETE = async (url, params) =>
  practicesGET(
    url,
    {},
    deleteRESTParams(params, { 'Content-Type': 'application/json' })
  )

/**
 * Adds shortcut properties and overrides to the passed in Course, Lesson, Topic, or Quiz object otherwise returns an empty object.
 * @param {Object} item Course item JSON object to copy and mutate.
 */
const cleanCourseItem = item =>
  item
    ? {
        ...item,
        description: item.content.rendered,
        clean_title: cleanTitle(item.title.rendered)
      }
    : {}

/**
 * Returns a Boolean value indicating whether or not the passed in JSON result is a 404 response
 * @param {Boolean} result
 */
const is404 = result =>
  result && result.data && result.data.status && result.data.status === 404

/**
 * Retrieves an array of LMS (i.e., WordPress) posts by ID.
 * If no results are found, an empty array is returned.
 * @param {Array[Number]} objectIds The post IDs to lookup.
 */
const getLMSObjectsByIds = async objectIds => {
  let objects = []

  // Use the LMS wp-json proxy to query the WP API
  const url = `${REMBLE_API_URL}/lms/posts`
  const result = await learnDashGET(url, {
    include: objectIds.join(',')
  })

  // Return an empty array if there are no recommended courses
  if (result?.length) objects = result

  return objects
}

/**
 * Retrieves a single LMS (i.e., WordPress) post by ID or null if not found.
 * @param {Number} objectId The post ID to lookup.
 */
const getLMSObjectById = async objectId => {
  // Use the LMS wp-json proxy to query the WP API
  const url = `${REMBLE_API_URL}/lms/posts`
  const result = await learnDashGET(url, {
    include: [objectId]
  })

  // Return null if no result
  if (!result?.length) return null

  // Return the first result
  return result[0]
}

/**
 * Retrieves an array of LMS (i.e., WordPress) categories by ID.
 * If no results are found, an empty array is returned.
 * @param {Array[Number]} categoryIds The category IDs to lookup.
 */
const getLMSCategoriesByIds = async categoryIds => {
  let categories = []

  // Use the LMS wp-json proxy to query the WP API
  const url = `${REMBLE_API_URL}/lms/categories`
  const result = await learnDashGET(url, {
    include: categoryIds.join(',')
  })

  // Return an empty array if there are no recommended courses
  if (result?.length) categories = result

  return categories
}

export {
  KEY_PRACTICE_AFFIRMATIONS,
  KEY_PRACTICE_BREATHWORK,
  KEY_PRACTICE_COPING_SKILLS,
  KEY_PRACTICE_DAILY_REMBLE,
  KEY_PRACTICE_JOURNALING,
  KEY_PRACTICE_MEDITATIONS,
  KEY_PRACTICE_RELATIONSHIPQA,
  KEY_PRACTICE_COMPLIMENTS,
  KEY_PRACTICE_DATE_IDEAS,
  KEY_PRACTICE_JOURNEYS,
  KEY_PRACTICE_SESSIONS,
  KEY_PREFERENCE_AFFIRMATIONS,
  KEY_PREFERENCE_BREATHWORK,
  KEY_PREFERENCE_COPING_SKILLS,
  KEY_PREFERENCE_JOURNALING,
  KEY_PREFERENCE_MEDITATIONS,
  KEY_PREFERENCE_RELATIONSHIPQA,
  KEY_PRACTICES_PREFERENCES_FIELD,
  KEY_PRACTICE_AFFIRMATIONS_WP,
  KEY_PRACTICE_BREATHWORK_WP,
  KEY_PRACTICE_COPING_SKILLS_WP,
  KEY_PRACTICE_DAILY_REMBLE_WP,
  KEY_ARTICLES_WP,
  KEY_PRACTICE_COMPLIMENTS_WP,
  KEY_PRACTICE_DATE_IDEAS_WP,
  KEY_PRACTICE_JOURNALING_WP,
  KEY_PRACTICE_MEDITATIONS_WP,
  KEY_PRACTICE_RELATIONSHIPQA_WP,
  NO_LIMIT,
  ERR_COULD_NOT_CONNECT_TO_SERVER,
  ERR_USERNAME_ALREADY_REGISTERED,
  ERR_USER_NOT_CONFIRMED,
  ERR_USER_ALREADY_CONFIRMED,
  ERR_CODE_JWT_INVALID,
  WP_API_URL,
  WP_API_PUBLIC_URL,
  REMBLE_WP_API_URL,
  REMBLE_API_URL,
  LMS_API_URL,
  TYPE_COURSE,
  TYPE_LESSON,
  TYPE_TOPIC,
  TYPE_QUIZ,
  SSE_EVENT_TYPE_MESSAGE,
  SSE_EVENT_TYPE_ERROR,
  STREAM_COMPLETE,
  buildRequestURL,
  debounce,
  getRESTParams,
  postRESTParams,
  learnDashGET,
  cleanCourseItem,
  is404,
  practicesGET,
  practicesPOST,
  practicesPATCH,
  practicesDELETE,
  getChatToken,
  getLMSObjectsByIds,
  getLMSObjectById,
  getLMSCategoriesByIds
}
